import React, { useRef, useState } from "react";
import { GraduationCap, Upload, Loader2, Play, CheckCircle2, AlertTriangle, Mic } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { waitTask, unlock, playOn } from "@/components/voiceKit";

let audioEl = null;

/** صف تدريب لشخصية واحدة: ارفع تسجيلًا → يُدرَّب ويُفعَّل كمصدر «مستنسخ» → استمع للنتيجة */
const TrainRow = ({ v, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const input = useRef(null);
  const ok = v.last_check?.ok;
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("الملف أكبر من 20 ميجابايت");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/voices/${v.id}/train`, fd);
      toast.info(`جارٍ تدريب «${v.name}» من تسجيل ${data.seconds} ثانية${data.looped ? " (قصير — كُرّر تلقائيًا، يُفضَّل 30 ثانية فأكثر)" : ""}…`);
      await waitTask(data.task_id);
      toast.success(`تم تدريب «${v.name}» ✅ — استمع للنتيجة بالزر المجاور`);
      onDone?.();
    } catch (err) { toast.error(err.message && !err.response ? err.message : formatError(err.response?.data?.detail)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const listen = async () => {
    if (!audioEl) audioEl = new Audio();
    unlock(audioEl);
    setPlaying(true);
    try {
      const end = Date.now() + 150000;
      while (Date.now() < end) {
        const r = await api.get(`/voices/${v.id}/sample`, { responseType: "blob" });
        if (r.status === 200) { playOn(audioEl, URL.createObjectURL(r.data)); break; }
        await new Promise((res) => setTimeout(res, 2500));
      }
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "تعذّر تشغيل العيّنة"); }
    finally { setPlaying(false); }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap rounded-xl border border-border bg-secondary/40 p-3" data-testid={`train-row-${v.id}`}>
      <div className="min-w-[140px]">
        <div className="font-bold text-sm">{v.name} <span className="text-xs text-muted-foreground font-normal">· {v.gender === "female" ? "امرأة" : "رجل"}</span></div>
        <div className="text-[11px] mt-0.5">
          {v.trained
            ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> مدرّب من تسجيلك{v.ref_name ? ` (${v.ref_name})` : ""}</span>
            : <span className="text-sky-600 dark:text-sky-400 font-semibold">صوت أساسي — لم يُدرَّب بعد</span>}
        </div>
        {v.last_check && <div className={`text-[11px] mt-0.5 flex items-center gap-1 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600"}`} data-testid={`train-health-${v.id}`}>
          {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {ok ? "آخر فحص: يعمل ومطابق" : `آخر فحص: ${v.last_check.error || "فشل"}`}
        </div>}
      </div>
      <div className="flex items-center gap-2 ms-auto">
        <input ref={input} type="file" accept="audio/*,video/mp4,.m4a,.mp3,.wav,.aac,.ogg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`train-file-${v.id}`} />
        <Button size="sm" onClick={() => input.current?.click()} disabled={busy} className="h-8 gap-1.5 text-xs" data-testid={`train-btn-${v.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {busy ? "يتدرّب…" : v.trained ? "استبدل التسجيل" : "ارفع تسجيلًا ودرّب"}
        </Button>
        <Button size="sm" variant="outline" onClick={listen} disabled={playing || busy} className="h-8 gap-1.5 text-xs border-border" data-testid={`train-listen-${v.id}`}>
          {playing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} استمع
        </Button>
      </div>
    </div>
  );
};

/** تدريب الشخصيات الصوتية — تحت قائمة الأصوات مباشرة، بلا فتح نافذة التعديل */
export const VoiceTrainingPanel = ({ voices = [], onChanged }) => (
  <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="voice-training-panel">
    <div>
      <div className="font-bold flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary" /> تدريب الشخصيات الصوتية</div>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex items-start gap-1"><Mic className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        لكل شخصية تسجيل واحد 30–90 ثانية (mp3 / m4a / wav، حتى 20 ميجابايت): غرفة هادئة، متحدث واحد، بلا موسيقى، نبرة إعلانية سعودية طبيعية. بعد التدريب تصبح الشخصية «مدرّبة» وتُعرض للعملاء أولًا، وتُفحص تلقائيًا بعد كل إطلاق.
      </p>
    </div>
    <div className="grid md:grid-cols-2 gap-2">
      {voices.map((v) => <TrainRow key={v.id} v={v} onDone={onChanged} />)}
    </div>
  </div>
);
